/**
 * Hook for managing conversation persistence
 *
 * Provides CRUD operations for conversations and messages.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  api,
  ConversationResponse,
  ConversationWithMessages,
  ConversationMessageInput,
  ConversationMessageResponse,
} from '../api';
import { queryKeys } from '../query-keys';

export interface UseConversationOptions {
  /** Arche ID for filtering conversations */
  archeId: string;
  /** Initial conversation ID to load */
  initialConversationId?: string | null;
  /** Callback when conversation changes */
  onConversationChange?: (conversationId: string | null) => void;
}

export interface UseConversationResult {
  /** Current conversation */
  conversation: ConversationWithMessages | null;
  /** List of user's conversations for this arche */
  conversations: ConversationResponse[];
  /** Whether data is loading */
  loading: boolean;
  /** Error if operation failed */
  error: Error | null;
  /** Create a new conversation */
  createConversation: (title?: string) => Promise<ConversationResponse | null>;
  /** Load an existing conversation */
  loadConversation: (conversationId: string) => Promise<void>;
  /** Add a message to current conversation */
  addMessage: (message: ConversationMessageInput) => Promise<ConversationMessageResponse[] | null>;
  /** Add multiple messages (for Multi-AI) */
  addMessages: (
    messages: ConversationMessageInput[],
  ) => Promise<ConversationMessageResponse[] | null>;
  /** Update conversation title */
  updateTitle: (title: string) => Promise<void>;
  /** Archive current conversation */
  archiveConversation: () => Promise<void>;
  /** Delete current conversation */
  deleteConversation: () => Promise<void>;
  /** Delete a specific conversation by ID */
  deleteConversationById: (conversationId: string) => Promise<void>;
  /** Clear current conversation (starts fresh) */
  clearConversation: () => void;
  /** Refresh conversation list */
  refreshList: () => Promise<void>;
  /** Export conversation */
  exportConversation: (format: 'json' | 'markdown') => Promise<string | null>;
}

/**
 * Hook for managing conversation persistence
 */
export function useConversation(options: UseConversationOptions): UseConversationResult {
  const { archeId, initialConversationId, onConversationChange } = options;
  const queryClient = useQueryClient();

  const [conversation, setConversation] = useState<ConversationWithMessages | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Use React Query for the conversation list
  const {
    data: listData,
    error: listError,
    refetch: refetchList,
  } = useQuery({
    queryKey: queryKeys.conversations.list(archeId),
    queryFn: () => api.listConversations({ archeId, status: 'active' }),
    enabled: !!archeId,
  });

  // Keep local conversations state synced with React Query data,
  // but allow optimistic updates via setConversations
  const [conversations, setConversations] = useState<ConversationResponse[]>([]);

  // Sync React Query data into local state
  useEffect(() => {
    if (listData?.conversations) {
      setConversations(listData.conversations);
    }
  }, [listData]);

  // Propagate list errors
  useEffect(() => {
    if (listError && isMountedRef.current) {
      setError(listError instanceof Error ? listError : new Error(String(listError)));
    }
  }, [listError]);

  // Load a specific conversation
  const loadConversation = useCallback(
    async (conversationId: string) => {
      setLoading(true);
      setError(null);

      try {
        const data = await api.getConversation(conversationId);
        if (isMountedRef.current) {
          setConversation(data);
          onConversationChange?.(conversationId);
        }
      } catch (err) {
        if (isMountedRef.current) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [onConversationChange],
  );

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (title?: string) => api.createConversation(archeId, title),
    onSuccess: (data) => {
      if (isMountedRef.current) {
        const newConversation: ConversationWithMessages = {
          ...data,
          messages: [],
        };
        setConversation(newConversation);
        setConversations((prev) => [data, ...prev]);
        onConversationChange?.(data.conversationId);
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.list(archeId) });
    },
    onError: (err) => {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    },
  });

  const createConversation = useCallback(
    async (title?: string): Promise<ConversationResponse | null> => {
      if (!archeId) return null;

      setLoading(true);
      setError(null);

      try {
        const data = await createMutation.mutateAsync(title);
        return data;
      } catch {
        return null;
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [archeId, createMutation],
  );

  // Helper to move a conversation to the top of the list (most recently updated)
  const moveConversationToTop = useCallback((conversationId: string) => {
    setConversations((prev) => {
      const index = prev.findIndex((c) => c.conversationId === conversationId);
      if (index <= 0) return prev; // Already at top or not found
      const conv = prev[index];
      const updated = {
        ...conv,
        updatedAt: new Date().toISOString(),
      };
      return [updated, ...prev.slice(0, index), ...prev.slice(index + 1)];
    });
  }, []);

  // Add message mutation
  const addMessageMutation = useMutation({
    mutationFn: ({
      conversationId,
      message,
    }: {
      conversationId: string;
      message: ConversationMessageInput;
    }) => api.addMessage(conversationId, message),
  });

  const addMessage = useCallback(
    async (message: ConversationMessageInput): Promise<ConversationMessageResponse[] | null> => {
      if (!conversation) return null;

      try {
        const result = await addMessageMutation.mutateAsync({
          conversationId: conversation.conversationId,
          message,
        });
        if (isMountedRef.current && result.messages) {
          setConversation((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              messages: [...prev.messages, ...result.messages],
              messageCount: prev.messageCount + result.messages.length,
            };
          });
          moveConversationToTop(conversation.conversationId);
        }
        return result.messages;
      } catch (err) {
        if (isMountedRef.current) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
        return null;
      }
    },
    [conversation, addMessageMutation, moveConversationToTop],
  );

  // Add multiple messages mutation
  const addMessagesMutation = useMutation({
    mutationFn: ({
      conversationId,
      messages,
    }: {
      conversationId: string;
      messages: ConversationMessageInput[];
    }) => api.addMessages(conversationId, messages),
  });

  const addMessages = useCallback(
    async (messages: ConversationMessageInput[]): Promise<ConversationMessageResponse[] | null> => {
      if (!conversation || messages.length === 0) return null;

      try {
        const result = await addMessagesMutation.mutateAsync({
          conversationId: conversation.conversationId,
          messages,
        });
        if (isMountedRef.current && result.messages) {
          setConversation((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              messages: [...prev.messages, ...result.messages],
              messageCount: prev.messageCount + result.messages.length,
            };
          });
          moveConversationToTop(conversation.conversationId);
        }
        return result.messages;
      } catch (err) {
        if (isMountedRef.current) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
        return null;
      }
    },
    [conversation, addMessagesMutation, moveConversationToTop],
  );

  // Update title mutation
  const updateTitleMutation = useMutation({
    mutationFn: ({ conversationId, title }: { conversationId: string; title: string }) =>
      api.updateConversation(conversationId, { title }),
    onSuccess: (updated, { conversationId }) => {
      if (isMountedRef.current) {
        setConversation((prev) => (prev ? { ...prev, title: updated.title } : prev));
        setConversations((prev) =>
          prev.map((c) =>
            c.conversationId === conversationId ? { ...c, title: updated.title } : c,
          ),
        );
      }
    },
    onError: (err) => {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    },
  });

  const updateTitle = useCallback(
    async (title: string) => {
      if (!conversation) return;
      await updateTitleMutation.mutateAsync({ conversationId: conversation.conversationId, title });
    },
    [conversation, updateTitleMutation],
  );

  // Archive mutation
  const archiveMutation = useMutation({
    mutationFn: (conversationId: string) =>
      api.updateConversation(conversationId, { status: 'archived' }),
    onSuccess: (_data, conversationId) => {
      if (isMountedRef.current) {
        setConversation(null);
        setConversations((prev) => prev.filter((c) => c.conversationId !== conversationId));
        onConversationChange?.(null);
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.list(archeId) });
    },
    onError: (err) => {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    },
  });

  const archiveConversation = useCallback(async () => {
    if (!conversation) return;
    await archiveMutation.mutateAsync(conversation.conversationId);
  }, [conversation, archiveMutation]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (conversationId: string) => api.deleteConversation(conversationId),
    onSuccess: (_data, conversationId) => {
      if (isMountedRef.current) {
        // If this was the active conversation, clear it
        setConversation((prev) => {
          if (prev?.conversationId === conversationId) {
            onConversationChange?.(null);
            return null;
          }
          return prev;
        });
        setConversations((prev) => prev.filter((c) => c.conversationId !== conversationId));
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.list(archeId) });
    },
    onError: (err) => {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    },
  });

  const deleteConversation = useCallback(async () => {
    if (!conversation) return;
    await deleteMutation.mutateAsync(conversation.conversationId);
  }, [conversation, deleteMutation]);

  const deleteConversationById = useCallback(
    async (conversationId: string) => {
      await deleteMutation.mutateAsync(conversationId);
    },
    [deleteMutation],
  );

  // Clear current conversation (local only)
  const clearConversation = useCallback(() => {
    setConversation(null);
    onConversationChange?.(null);
  }, [onConversationChange]);

  // Refresh conversation list
  const refreshList = useCallback(async () => {
    await refetchList();
  }, [refetchList]);

  // Export conversation
  const exportConversation = useCallback(
    async (format: 'json' | 'markdown'): Promise<string | null> => {
      if (!conversation) return null;

      try {
        return await api.exportConversation(conversation.conversationId, format);
      } catch (err) {
        if (isMountedRef.current) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
        return null;
      }
    },
    [conversation],
  );

  // Load initial conversation if provided
  useEffect(() => {
    if (initialConversationId) {
      loadConversation(initialConversationId);
    }
  }, [initialConversationId, loadConversation]);

  return {
    conversation,
    conversations,
    loading,
    error,
    createConversation,
    loadConversation,
    addMessage,
    addMessages,
    updateTitle,
    archiveConversation,
    deleteConversation,
    deleteConversationById,
    clearConversation,
    refreshList,
    exportConversation,
  };
}

export default useConversation;
